import React, { useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';

// Components
import SplashScreen from '~features/general/SplashScreen';
import Toaster from '~features/toast';
import Notification from './tasklist/Notification';
import OrderFeedbackModal from '~features/modals/OrderFeedbackModal';
import ExternalFeedbackModal from '~features/modals/ExternalFeedbackModal';
import TroubleReportModal from '~features/modals/TroubleReportModal';
import RequestPermissionsModal from '~features/modals/RequestPermissionsModal';
import ChangePhoneNumberModal from '~features/modals/ChangePhoneNumberModal';
import ErrorNotification from './ErrorNotification';
import TopNav from './TopNav';
import SectionNav from './sideNav/SectionNav';
import ProfileDrawer from '~features/drawer/ProfileDrawer';
import NotificationsDrawer from '~features/drawer/NotificationsDrawer';
import PrintOrder from '~features/printOrder';
import BreadCrumbs from '~features/breadcrumbs';
import LinkInfo from './LinkInfo';

// Hooks
import useWidthSetter from './useWidthSetter';

// Utils
import { topNavHeight } from '~utils/common.utils';

// Styles
import '~styles/index.scss';

// Global styled-components styles
import GlobalStyles from '~styles/GlobalStyles';

// Actions
import { updateTaskList } from '~actions/transferActions';
import { initializeApp, setWindowInfo } from '~actions/rootActions';
import { getUser } from '~features/user/ducks';
import { isPrintView } from '../features/printOrder/ducks';

const App = props => {
  const {
    user,
    initializeApp,
    setWindowInfo,
    isMobileFullscreen,
    projectOrderLink,
    isDesktop,
  } = props;

  const appInitialized = useSelector(state => state.reducer.appInitialized);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Populate Redux store with LOTS of stuff
    initializeApp(navigate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use custom hook to update window width to Redux
  // Only triggers when we transition from desktop to mobile and vice versa
  useWidthSetter(setWindowInfo);

  return (
    <>
      {appInitialized && <TopNav />}
      <div className="container fill">
        <div style={{ flex: '1' }}>
          <GlobalStyles />
          {/* Splash fades itself out based on init status */}
          {!__TEST__ && <SplashScreen show={!appInitialized} />}
          <ErrorNotification />
          <BreadCrumbs />

          {/* Portal container for mobile specific views */}
          <MobileFullscreenView id="mobile-fullscreen" />

          {/* Show other content after init is done */}

          {props.isPrintView && <PrintOrder />}

          {appInitialized && (
            <div
              style={{
                display: props.isPrintView ? 'none' : 'block',
              }}
            >
              <Toaster />
              <Notification />

              <OrderFeedbackModal />
              <ExternalFeedbackModal />
              <TroubleReportModal />
              <RequestPermissionsModal />
              <ChangePhoneNumberModal />

              <ProfileDrawer />
              <NotificationsDrawer />

              <Layout>
                <SectionNav home={location.pathname === '/'} />
                {/* If we're in full screen nav in mobile, don't show content */}
                {!(location.pathname === '/' && !isDesktop) && (
                  <Content
                    style={{
                      // Hide content if we're on fullscreen
                      // This is mainly for a11y, so if this causes problems
                      // just take it off
                      display: isMobileFullscreen && 'none',
                    }}
                  >
                    {projectOrderLink && (
                      <LinkInfo projectOrderLink={projectOrderLink} />
                    )}
                    {props.children}
                  </Content>
                )}
              </Layout>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const Layout = styled.div`
  display: flex;
`;

const Content = styled.div`
  background: #fff;
  padding: 5px;
  width: 100%;

  &:empty {
    visibility: hidden;
  }

  flex: 1 1 auto;

  @media (max-width: 768px) {
    padding: 2px;
  }

  @media (min-width: 768px) {
    margin: ${topNavHeight}px 0 100px 15px;
    box-shadow: 1px 2px 16px 0px rgba(200, 200, 200, 1);
    padding: 40px;
  }

  @media (max-width: 991px) {
    margin-top: 0;
    margin-left: 0;
  }
`;

const MobileFullscreenView = styled.div``;

export default connect(
  state => ({
    lastUpdated: state.transferReducers.lastUpdated,
    user: getUser(state),
    isPrintView: isPrintView(state),
    errorMsgVisible: state.errorReducer.errorMsgVisible,
    isMobileFullscreen: state.reducer.isMobileFullscreen,
    projectOrderLink: state.projects.projectOrderLink,
    isDesktop: state.reducer.isDesktop,
  }),
  {
    updateTaskList,
    initializeApp,
    setWindowInfo,
  }
)(App);
